import * as React from 'react'

import { useAdmin } from '../../providers/AdminProvider'
import { Radar } from '../../gql/graphql'
import { useRadarItem } from './useRadarItem'
import { ExternalLink, Copy, Check } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Textarea } from '@/components/ui/textarea'
import { TagsInput } from '@/components/ui/tags-input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import NextStatusButton from './NextStatusButton'
import { useCopy } from '../../hooks/useCopy'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

const statuses = [
  { id: 'NEW', title: 'Új' },
  { id: 'IN_PROGRESS', title: 'Folyamatban' },
  { id: 'PROBLEMS', title: 'Problémás' },
  { id: 'DONE', title: 'Készen van' },
  { id: 'APPROVED', title: 'Jóváhagyva' },
  { id: 'SENT', title: 'Kiküldve' },
]

const importanceOptions = [
  { value: 'FONTOS_LEHET', label: 'Fontos lehet' },
  { value: 'FONTOS', label: 'Fontos' },
  { value: 'NEM_FONTOS', label: 'Nem fontos' },
]

const RadarItem2 = ({
  value,
  open: _initOpen = true,
  admins,
  onSave,
}: {
  value: Radar
  open: boolean
  admins: any[]
  onSave?: () => void
}) => {
  const { isAdmin } = useAdmin()
  const { draft, isDirty, handleChange, save } = useRadarItem(value)

  const {
    id,
    baseAct: { title, text },
    journal: { issueYear, issueNumber, issueDate, gazetteUrl },
    njtUrl,
    affectedCustomers,
  } = value

  const { status, importance, digest, assignedTo } = draft
  const { copied: titleCopied, copy: copyTitle } = useCopy(`${text} ${title}`)

  return (
    <Card>
      <CardContent className="p-4 flex flex-col gap-4">
        <div className="text-xs text-muted-foreground">{id}</div>

        <h5 className="font-bold text-gray-900 dark:text-white">
          <a href={njtUrl || ''} target="_blank" rel="noreferrer">
            {text} {title}
            <ExternalLink className="inline h-4 w-4 ml-1 align-middle shrink-0" />
          </a>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="inline-flex h-6 w-6 ml-1 align-middle"
                  onClick={copyTitle}
                >
                  {titleCopied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>Hivatkozás másolása</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </h5>

        {/* Közlöny + érintett ügyfelek */}
        <div className="flex flex-col gap-3 md:flex-row md:gap-6 md:items-start">
          <div className="flex flex-col gap-1">
            <Label>Közlöny</Label>
            <a href={gazetteUrl || ''} target="_blank" rel="noreferrer" className="text-sm">
              {`MK ${issueYear}/${issueNumber}. (${issueDate})`}
              <ExternalLink className="inline h-3 w-3 ml-1 align-middle shrink-0" />
            </a>
          </div>

          {isAdmin() && (
            <>
              <Separator orientation="vertical" className="hidden md:block self-stretch" />
              <div className="flex flex-col gap-1">
                <Label>Érintett ügyfelek</Label>
                <div className="flex gap-1 flex-wrap">
                  {affectedCustomers.map((customer) => (
                    <Badge key={customer.name}>
                      {customer.name}
                      {customer.lists.length > 1 ? ` (${customer.lists.length})` : ''}
                    </Badge>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Státusz + fontosság + delegálva */}
        <div className="flex flex-col gap-3 lg:flex-row lg:gap-6 lg:items-start">
          <div className="flex flex-col gap-1">
            <Label>Státusz</Label>
            <div className="flex items-center gap-2">
              <Select
                value={status}
                onValueChange={(v) => handleChange('status', v as typeof status)}
              >
                <SelectTrigger
                  className={cn('w-full sm:w-40', {
                    'bg-orange-50 border-orange-200 text-orange-800': status === 'DONE',
                    'bg-blue-50 border-blue-200 text-blue-800':
                      status === 'IN_PROGRESS' || status === 'NEW',
                    'bg-red-50 border-red-200 text-red-800': status === 'PROBLEMS',
                    'bg-green-50 border-green-200 text-green-800': status === 'APPROVED',
                  })}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statuses.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <NextStatusButton status={status} onChange={(v) => handleChange('status', v)} />
            </div>
          </div>

          <Separator orientation="vertical" className="hidden lg:block self-stretch" />

          <div className="flex flex-col gap-1">
            <Label>Fontosság</Label>
            <RadioGroup
              value={importance ?? undefined}
              onValueChange={(v) => handleChange('importance', v as typeof importance)}
              className="flex flex-row gap-4 pt-1"
            >
              {importanceOptions.map((opt) => (
                <div key={opt.value} className="flex items-center gap-1.5">
                  <RadioGroupItem value={opt.value} id={`importance-${opt.value}-${id}`} />
                  <Label htmlFor={`importance-${opt.value}-${id}`} className="font-normal">
                    {opt.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {isAdmin() && (
            <>
              <Separator orientation="vertical" className="hidden lg:block self-stretch" />
              <div className="flex flex-col gap-1">
                <Label>Delegálva</Label>
                <Select
                  value={assignedTo?.toString() ?? ''}
                  onValueChange={(v) => handleChange('assignedTo', v ? Number(v) : null)}
                >
                  <SelectTrigger className="w-full sm:w-40">
                    <SelectValue placeholder="–" />
                  </SelectTrigger>
                  <SelectContent>
                    {admins.map((item) => (
                      <SelectItem key={item.id} value={item.id.toString()}>
                        {item.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <Label>Címkék</Label>
          <TagsInput
            value={draft.tags ?? []}
            placeholder="Új címke..."
            onChange={(tags) => handleChange('tags', tags)}
          />
        </div>

        {/* Összefoglaló */}
        <div className="flex flex-col gap-1">
          <Label>Összefoglaló</Label>
          <Textarea
            placeholder="Ide írd az összefoglalót."
            value={digest ?? ''}
            onChange={(e) => handleChange('digest', e.target.value)}
            className="min-h-[100px]"
          />
        </div>

        <div className="flex flex-col gap-1">
          <Label>Hatályos / hatálytalan</Label>
          <TagsInput
            value={draft.enterIntoForce ?? []}
            placeholder="pl.: 2023-05-12"
            onChange={(enterIntoForce) => handleChange('enterIntoForce', enterIntoForce)}
          />
        </div>

        {/* Mentés */}
        <div className="flex flex-row items-center gap-2">
          {isDirty && (
            <Button
              onClick={async () => {
                await save()
                onSave?.()
              }}
            >
              Ment
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

export default React.memo(RadarItem2)
